/**
 *
 * @module adEngineManager
 *
 */

import Check from 'check-types';

import Token from './../../token/entry';
import Services from './../../services/entry';
import Logging from './../../logging/entry';

import AdvertisingIdProvider from './advertisingIdProvider';

import AdEngineClient from './../../services/media/adEngine/adEngineClient';
import GeoProvider from './../../platforms/geoProvider';

const AdEngineManagerConfiguration = Services.Configuration.AdEngineManagerConfiguration;
const TokenManager = Token.TokenManager;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyObject,
    object,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class AdEngineManager
 * @since 3.6.0
 *
 */
export default class AdEngineManager {

    /**
     *
     * @param {Object} options
     * @param {SDK.Services.Configuration.AdEngineManagerConfiguration} options.config
     * @param {SDK.Services.Media.AdEngine.AdEngineClient} options.client
     * @param {SDK.Token.TokenManager} options.tokenManager
     * @param {String} options.sdkver
     * @param {SDK.Logging.Logger} options.logger
     * @param {GeoProvider} [options.geoProvider=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: 'AdEngineManager(options)',
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get config() {
                return `${this.ctor} needs SDK.Services.Configuration.AdEngineManagerConfiguration instance`;
            },
            get client() {
                return `${this.ctor} needs SDK.Services.Media.AdEngine.AdEngineClient instance`;
            },
            get tokenManager() {
                return `${this.ctor} needs SDK.Token.TokenManager instance`;
            },
            get geoProvider() {
                return `${this.ctor} needs GeoProvider instance`;
            },
            get sdkver() {
                return `${this.ctor} options.sdkver should be a String with length`;
            },
            get advertisingIdProvider() {
                return `${this.ctor} needs SDK.Media.AdEngine.AdvertisingIdProvider`;
            },
            get adTargeting() {
                return `${this.ctor} options.adTargeting should be an Object`;
            },
            get logger() {
                return `${this.ctor} needs SDK.Logging.Logger instance`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { config, client, tokenManager, sdkver, logger, geoProvider = null } = options;
        const { extras = {} } = config;
        const { adTargeting = {} } = extras;

        instanceStrict(config, AdEngineManagerConfiguration, errorMessage.config, InvalidArgumentException);
        instanceStrict(client, AdEngineClient, errorMessage.client, InvalidArgumentException);

        instanceStrict(
            tokenManager,
            TokenManager,
            errorMessage.tokenManager,
            InvalidArgumentException
        );

        nonEmptyString(sdkver, errorMessage.sdkver, InvalidArgumentException);
        instanceStrict(logger, Logging.Logger, errorMessage.logger, InvalidArgumentException);

        if (Check.assigned(geoProvider)) {
            instanceStrict(geoProvider, GeoProvider, errorMessage.geoProvider, InvalidArgumentException);
        }

        object(adTargeting, errorMessage.adTargeting, InvalidArgumentException);

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.AdEngineManagerConfiguration}
         *
         */
        this.config = config;

        /**
         *
         * @access private
         * @type {SDK.Services.Media.AdEngine.AdEngineClient}
         *
         */
        this.client = client;

        /**
         *
         * @access private
         * @type {SDK.Token.TokenManager}
         *
         */
        this.tokenManager = tokenManager;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {SDK.Media.AdEngine.AdvertisingIdProvider}
         * @desc the AdvertisingIdProvider provides device-specific advertising information for the adEngine team
         *
         */
        this.advertisingIdProvider = new AdvertisingIdProvider();

        /**
         *
         * @access private
         * @type {SDK.GeoProvider}
         *
         */
        this.geoProvider = Check.assigned(geoProvider) ? geoProvider : new GeoProvider(logger);

        /**
         *
         * @access private
         * @type {Object}
         * @desc contains some static values used for AdEngine tracking - pulled from the SDK Config
         * @note adTargeting comes from adEngine.extras.adTargeting
         * @note sdver comes from deviceManger.environmentConfiguration.sdkVersion,
         *
         */
        this.adTargeting = Object.assign({}, adTargeting, { sdkver });

        /**
         *
         * @access private
         * @type {Boolean}
         *
         */
        this.disabled = this.config.disabled;

        this.logger.log(AdEngineManager.name, 'Created.');

    }

    /**
     *
     * @access private
     * @param {Object} options
     * @desc returns set-cookie headers from the service that are stored in the device's cookie jar
     * @note this method needs to catch a failure in order to prevent playback from failing
     * @returns {Promise<Void>}
     *
     */
    updateCookies(options) {

        const errorMessage = {
            ctor: 'AdEngineManager.updateCookies(options)',
            options: () => {
                return `${this.ctor} options should be a non-empty Object`;
            }
        };

        object(options, errorMessage.options, InvalidArgumentException);

        const { client, accessToken, logger } = this;

        return this.constructAdEngineData(options).then((adEngineObject) => {

            return client.updateCookies(accessToken, adEngineObject).catch((ex) => {

                logger.warn(AdEngineManager.name, ex);

                return Promise.resolve();

            });

        });

    }

    /**
     *
     * @access private
     * @param {Object} options
     * @note this method needs to catch a failure in order to prevent playback from failing
     * @note in the event of a failure we return an empty Array
     * @note this method is not currently used but exists to align with the spec
     * @returns {Promise<Cookies<Array>|Array>}
     *
     */
    getCookies(options) {

        const errorMessage = {
            ctor: 'AdEngineManager.getCookies(options)',
            options: () => {
                return `${this.ctor} options should be a non-empty Object`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { client, accessToken, logger } = this;

        return this.constructAdEngineData(options).then((adEngineObject) => {

            return client.getCookies(accessToken, adEngineObject).catch((ex) => {

                logger.warn(AdEngineManager.name, ex);

                return Promise.resolve([]);

            });

        });

    }

    /** region private */

    /**
     *
     * @param {Object} options
     * @desc gets geoLocation and merges various tracking information into a temporary, flat object
     * @note xny needs to be a String w/o spaces - e.g. '[1,2]'
     * @note xny should default to undefined to avoid being serialized in the client
     * @note xny should be omitted if 0,0 i.e. not provided
     * @note lat maps true to 1 and false to 0 - must be 0|1
     * @note all adEngine query param keys are lowercase not camelCase
     * @note IMPORTANT: when constructing the adEngine object make sure that `this.adTargeting` is merged last to
     * avoid app developers from overriding values from the config
     * @returns {Promise<Object>}
     *
     */
    constructAdEngineData(options) {

        const errorMessage = {
            method: 'constructAdEngineData(options)',
            get options() {
                return `${this.method} options should be a non-empty Object`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { advertisingIdProvider, adTargeting } = this;

        return this.geoProvider.getGeoLocation().then((geoLocation) => {

            const { latitude, longitude } = geoLocation;

            const coordinates = `[${latitude},${longitude}]`;
            const advertisingId = advertisingIdProvider.getId();
            const limitAdTracking = advertisingIdProvider.limitTracking() ? 1 : 0;
            const sdkGenerated = {
                devid: advertisingId,
                lat: limitAdTracking,
                xny: coordinates !== '[0,0]' ? coordinates : undefined
            };

            const adEngineObject = Object.assign({}, sdkGenerated, options, adTargeting);

            return Promise.resolve(adEngineObject);

        });

    }

    /**
     *
     * @access private
     * @desc Grabs a fresh AccessToken from the AccessTokenProvider instance
     * @returns {SDK.Token.AccessToken}
     *
     */
    get accessToken() {

        return this.tokenManager.getAccessToken();

    }

}



// WEBPACK FOOTER //
// ./src/media/adEngine/adEngineManager.js